import { useEffect, useState } from "react";
import { MdRefresh, MdSave } from "react-icons/md";
import ToggleSwitch from "components/ui/ToggleSwitch";
import Select from "components/ui/Select";
import { Skeleton } from "components/ui/Skeleton";
import { useAppLanguage } from "hooks/useAppLanguage";
import { useCourse } from "../../context/CourseContext";
import {
  instructorCourseVideoPlayerSettingsService,
  type VideoPlayerSettingsPayload,
} from "services/instructorCourseVideoPlayerSettingsService";
import { toastError, toastSuccess } from "lib/toast";

const DEFAULT_FORM: VideoPlayerSettingsPayload = {
  video_voice_announcement_enabled: true,
  video_voice_announcement_interval_seconds: 5,
  video_voice_announcement_include_student_id: true,
  video_voice_announcement_template_ar: "هذا الفيديو مخصص للطالب {name} رقم {id}",
  video_voice_announcement_template_en: "This video is assigned to {name}, ID {id}",
  video_watermark_enabled: true,
  video_watermark_visible_seconds: 5,
  video_watermark_hidden_seconds: 10,
  video_watermark_opacity: "medium",
};

export default function VideoPlayerSettingsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const { course } = useCourse();

  const [form, setForm] = useState<VideoPlayerSettingsPayload>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const labels = {
    title: isArabic ? "إعدادات مشغل الفيديو" : "Video Player Settings",
    subtitle: isArabic
      ? "إعدادات الأمان والعلامة المائية والإعلان الصوتي لهذا الكورس فقط."
      : "Security, watermark, and voice announcement settings for this course only.",
    voiceSection: isArabic ? "الإعلان الصوتي" : "Voice Announcement",
    watermarkSection: isArabic ? "العلامة المائية" : "Watermark",
    enableVoice: isArabic ? "تفعيل نطق اسم الطالب" : "Enable student voice announcement",
    interval: isArabic ? "مدة تكرار الصوت" : "Voice repeat interval",
    includeId: isArabic ? "تضمين رقم الطالب" : "Include student ID",
    templateAr: isArabic ? "قالب الصوت بالعربية" : "Arabic voice template",
    templateEn: isArabic ? "قالب الصوت بالإنجليزية" : "English voice template",
    enableWatermark: isArabic ? "تفعيل العلامة المائية" : "Enable watermark",
    visibleSeconds: isArabic ? "مدة ظهور العلامة المائية" : "Watermark visible duration",
    hiddenSeconds: isArabic ? "مدة اختفاء العلامة المائية" : "Watermark hidden duration",
    opacity: isArabic ? "شفافية العلامة المائية" : "Watermark opacity",
    save: isArabic ? "حفظ الإعدادات" : "Save settings",
    reset: isArabic ? "استعادة الافتراضي" : "Reset to defaults",
    placeholdersHint: isArabic
      ? "استخدم {name} و {id} و {phone} كمتغيرات."
      : "Use {name}, {id}, and {phone} as placeholders.",
    intervalHint: isArabic ? "من 5 إلى 3600 ثانية" : "Between 5 and 3600 seconds",
    visibleHint: isArabic ? "من 1 إلى 60 ثانية" : "Between 1 and 60 seconds",
    hiddenHint: isArabic ? "من 1 إلى 300 ثانية" : "Between 1 and 300 seconds",
  };

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const data = await instructorCourseVideoPlayerSettingsService.get(course.id);
        setForm({ ...DEFAULT_FORM, ...data });
      } catch {
        toastError(isArabic ? "فشل تحميل الإعدادات" : "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [course.id, isArabic]);

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    const interval = form.video_voice_announcement_interval_seconds;
    const visible = form.video_watermark_visible_seconds;
    const hidden = form.video_watermark_hidden_seconds;

    if (interval < 5 || interval > 3600) {
      next.video_voice_announcement_interval_seconds = isArabic
        ? "يجب أن تكون المدة بين 5 و 3600 ثانية"
        : "Interval must be between 5 and 3600 seconds";
    }

    if (visible < 1 || visible > 60) {
      next.video_watermark_visible_seconds = isArabic
        ? "يجب أن تكون مدة الظهور بين 1 و 60 ثانية"
        : "Visible duration must be between 1 and 60 seconds";
    }

    if (hidden < 1 || hidden > 300) {
      next.video_watermark_hidden_seconds = isArabic
        ? "يجب أن تكون مدة الاختفاء بين 1 و 300 ثانية"
        : "Hidden duration must be between 1 and 300 seconds";
    }

    if (!form.video_voice_announcement_template_ar.trim()) {
      next.video_voice_announcement_template_ar = isArabic
        ? "القالب العربي مطلوب"
        : "Arabic template is required";
    }

    if (!form.video_voice_announcement_template_en.trim()) {
      next.video_voice_announcement_template_en = isArabic
        ? "القالب الإنجليزي مطلوب"
        : "English template is required";
    }

    return next;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setSaving(true);
    try {
      const updated = await instructorCourseVideoPlayerSettingsService.update(course.id, form);
      setForm({ ...DEFAULT_FORM, ...updated });
      toastSuccess(isArabic ? "تم حفظ الإعدادات بنجاح." : "Settings saved successfully.");
    } catch (error: any) {
      const serverErrors = error?.response?.data?.errors as Record<string, string[]> | undefined;
      if (serverErrors) {
        const mapped: Record<string, string> = {};
        for (const [key, messages] of Object.entries(serverErrors)) {
          mapped[key] = messages[0] ?? (isArabic ? "قيمة غير صالحة" : "Invalid value");
        }
        setErrors(mapped);
      }
      toastError(
        error?.response?.data?.message ||
          (isArabic ? "فشل حفظ الإعدادات" : "Failed to save settings")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({ ...DEFAULT_FORM });
    setErrors({});
  };

  const updateField = <K extends keyof VideoPlayerSettingsPayload>(
    key: K,
    value: VideoPlayerSettingsPayload[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">{labels.title}</h2>
        <p className="text-sm text-gray-500">{labels.subtitle}</p>
      </header>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="space-y-6">
          <div>
            <h3 className="mb-4 text-base font-semibold text-gray-800 dark:text-white">
              {labels.voiceSection}
            </h3>

            <div className="mb-4 flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {labels.enableVoice}
              </span>
              <ToggleSwitch
                checked={form.video_voice_announcement_enabled}
                onChange={(checked) => updateField("video_voice_announcement_enabled", checked)}
              />
            </div>

            <div className="mb-4 flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {labels.includeId}
              </span>
              <ToggleSwitch
                checked={form.video_voice_announcement_include_student_id}
                onChange={(checked) =>
                  updateField("video_voice_announcement_include_student_id", checked)
                }
              />
            </div>

            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              {labels.interval}
            </label>
            <input
              type="number"
              min={5}
              max={3600}
              value={form.video_voice_announcement_interval_seconds}
              onChange={(e) =>
                updateField(
                  "video_voice_announcement_interval_seconds",
                  Number(e.target.value) || 0
                )
              }
              className={`mb-1 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white ${
                errors.video_voice_announcement_interval_seconds ? "border-red-500" : "border-gray-200"
              }`}
            />
            <p className="mb-1 text-xs text-gray-500">{labels.intervalHint}</p>
            {errors.video_voice_announcement_interval_seconds && (
              <p className="mb-3 text-xs text-red-600">
                {errors.video_voice_announcement_interval_seconds}
              </p>
            )}

            <label className="mb-1 mt-3 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              {labels.templateAr}
            </label>
            <textarea
              rows={2}
              value={form.video_voice_announcement_template_ar}
              onChange={(e) => updateField("video_voice_announcement_template_ar", e.target.value)}
              className={`mb-1 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white ${
                errors.video_voice_announcement_template_ar ? "border-red-500" : "border-gray-200"
              }`}
              dir="rtl"
            />
            {errors.video_voice_announcement_template_ar && (
              <p className="mb-3 text-xs text-red-600">
                {errors.video_voice_announcement_template_ar}
              </p>
            )}

            <label className="mb-1 mt-3 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              {labels.templateEn}
            </label>
            <textarea
              rows={2}
              value={form.video_voice_announcement_template_en}
              onChange={(e) => updateField("video_voice_announcement_template_en", e.target.value)}
              className={`mb-1 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white ${
                errors.video_voice_announcement_template_en ? "border-red-500" : "border-gray-200"
              }`}
              dir="ltr"
            />
            <p className="text-xs text-gray-500">{labels.placeholdersHint}</p>
            {errors.video_voice_announcement_template_en && (
              <p className="mt-1 text-xs text-red-600">
                {errors.video_voice_announcement_template_en}
              </p>
            )}
          </div>

          <div>
            <h3 className="mb-4 text-base font-semibold text-gray-800 dark:text-white">
              {labels.watermarkSection}
            </h3>

            <div className="mb-4 flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {labels.enableWatermark}
              </span>
              <ToggleSwitch
                checked={form.video_watermark_enabled}
                onChange={(checked) => updateField("video_watermark_enabled", checked)}
              />
            </div>

            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              {labels.visibleSeconds}
            </label>
            <input
              type="number"
              min={1}
              max={60}
              value={form.video_watermark_visible_seconds}
              onChange={(e) =>
                updateField("video_watermark_visible_seconds", Number(e.target.value) || 0)
              }
              className={`mb-1 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white ${
                errors.video_watermark_visible_seconds ? "border-red-500" : "border-gray-200"
              }`}
            />
            <p className="mb-1 text-xs text-gray-500">{labels.visibleHint}</p>
            {errors.video_watermark_visible_seconds && (
              <p className="mb-3 text-xs text-red-600">{errors.video_watermark_visible_seconds}</p>
            )}

            <label className="mb-1 mt-3 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              {labels.hiddenSeconds}
            </label>
            <input
              type="number"
              min={1}
              max={300}
              value={form.video_watermark_hidden_seconds}
              onChange={(e) =>
                updateField("video_watermark_hidden_seconds", Number(e.target.value) || 0)
              }
              className={`mb-1 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white ${
                errors.video_watermark_hidden_seconds ? "border-red-500" : "border-gray-200"
              }`}
            />
            <p className="mb-1 text-xs text-gray-500">{labels.hiddenHint}</p>
            {errors.video_watermark_hidden_seconds && (
              <p className="mb-3 text-xs text-red-600">{errors.video_watermark_hidden_seconds}</p>
            )}

            <Select
              label={labels.opacity}
              value={form.video_watermark_opacity}
              onChange={(next) =>
                updateField(
                  "video_watermark_opacity",
                  String(next) as VideoPlayerSettingsPayload["video_watermark_opacity"]
                )
              }
              options={[
                { value: "low", label: isArabic ? "منخفضة" : "Low" },
                { value: "medium", label: isArabic ? "متوسطة" : "Medium" },
                { value: "high", label: isArabic ? "عالية" : "High" },
              ]}
              isArabic={isArabic}
              allowEmpty={false}
              clearable={false}
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
            >
              <MdRefresh className="h-4 w-4" />
              {labels.reset}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
            >
              <MdSave className="h-5 w-5" />
              {saving ? (isArabic ? "..." : "...") : labels.save}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
